import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  ReactNode,
} from "react";
import { HUBSPOT_SCRIPT_URL } from "../lib/hubspot-config";
import { conversationsSettings } from "../lib/conversations-settings";
import "./hubspot-chat.css";

type UserInfo = {
  email: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  chatIdentificationToken?: string | null;
};

type HubSpotContextValue = {
  isLoaded: boolean;
  isLoading: boolean;
  isIdentified: boolean;
  error: string | null;
  unreadCount: number;
  isChatOpen: boolean;
  openChat: () => void;
  closeChat: () => void;
  toggleChat: () => void;
  identifyUser: (user: UserInfo) => void;
};

const HubSpotContext = createContext<HubSpotContextValue | null>(null);

type Props = {
  children: ReactNode;
};

export const HubSpotProvider = ({ children }: Props) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isIdentified, setIsIdentified] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const scriptLoadedRef = useRef(false);
  const handlersRegisteredRef = useRef(false);
  const widgetLoadingRef = useRef(false);

  // Load the HubSpot script - only called after user is identified
  const loadScript = useCallback(() => {
    // Handler for unread message count changes
    const unreadHandler = (payload: unknown) => {
      const count =
        typeof payload === "object" &&
        payload !== null &&
        "unreadCount" in payload
          ? (payload as { unreadCount: number }).unreadCount
          : 0;
      setUnreadCount(count);
    };

    // Auto-hide bubble when user closes the chat widget
    const widgetClosedHandler = () => {
      if (window.HubSpotConversations) {
        try {
          window.HubSpotConversations.widget.remove();
        } catch {
          // Ignore errors during widget removal
        }
      }
      setIsChatOpen(false);
    };

    const registerHandlers = (
      api: NonNullable<typeof window.HubSpotConversations>,
    ) => {
      if (handlersRegisteredRef.current) return;
      handlersRegisteredRef.current = true;
      api.on("unreadConversationCountChanged", unreadHandler);
      api.on("widgetClosed", widgetClosedHandler);
    };

    // If script is already loaded (by HubSpotTracking), just set up handlers
    if (
      scriptLoadedRef.current ||
      document.getElementById("hs-script-loader")
    ) {
      scriptLoadedRef.current = true;

      // If HubSpot API is already available, set up handlers immediately
      if (window.HubSpotConversations) {
        setIsLoaded(true);
        registerHandlers(window.HubSpotConversations);
      } else {
        // Script loaded but API not ready yet - wait for it
        setIsLoading(true);
        window.hsConversationsOnReady = window.hsConversationsOnReady || [];
        window.hsConversationsOnReady.push(() => {
          setIsLoaded(true);
          setIsLoading(false);
          if (window.HubSpotConversations) {
            registerHandlers(window.HubSpotConversations);
          }
        });
      }
      return;
    }

    scriptLoadedRef.current = true;
    setIsLoading(true);

    // Set up callback for when HubSpot is ready
    window.hsConversationsOnReady = [
      () => {
        setIsLoaded(true);
        setIsLoading(false);

        if (window.HubSpotConversations) {
          registerHandlers(window.HubSpotConversations);
        }
      },
    ];

    // Load the HubSpot script
    const script = document.createElement("script");
    script.src = HUBSPOT_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.id = "hs-script-loader";
    script.type = "text/javascript";

    script.onerror = () => {
      setError("Failed to load HubSpot chat");
      setIsLoading(false);
    };

    document.body.appendChild(script);

    // Timeout fallback
    setTimeout(() => {
      if (!window.HubSpotConversations) {
        setError("HubSpot chat initialization timed out");
        setIsLoading(false);
      }
    }, 15000);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (window.HubSpotConversations) {
        try {
          window.HubSpotConversations.widget.remove();
        } catch {
          // Ignore errors during cleanup
        }
      }
    };
  }, []);

  const openChat = useCallback(() => {
    if (!window.HubSpotConversations) {
      return;
    }

    try {
      const status = window.HubSpotConversations.widget.status();

      if (!status.loaded) {
        if (!widgetLoadingRef.current) {
          widgetLoadingRef.current = true;
          window.HubSpotConversations.on("widgetLoaded", () => {
            widgetLoadingRef.current = false;
            window.HubSpotConversations?.widget.open();
            setIsChatOpen(true);
          });
        }
        window.HubSpotConversations.widget.load();
      } else {
        window.HubSpotConversations.widget.open();
        setIsChatOpen(true);
      }
    } catch (e) {
      console.error("[HubSpot] Error opening chat:", e);
    }
  }, []);

  const closeChat = useCallback(() => {
    if (window.HubSpotConversations) {
      try {
        window.HubSpotConversations.widget.remove();
      } catch {
        // Ignore errors during widget removal
      }
      setIsChatOpen(false);
    }
  }, []);

  const toggleChat = useCallback(() => {
    if (isChatOpen) {
      closeChat();
    } else {
      openChat();
    }
  }, [isChatOpen, closeChat, openChat]);

  const identifyUser = useCallback(
    (user: UserInfo) => {
      // If HubSpot is loaded and user email changed, clear the session
      if (window.HubSpotConversations) {
        const currentEmail =
          window.hsConversationsSettings?.identificationEmail;
        if (currentEmail && currentEmail !== user.email) {
          try {
            window.HubSpotConversations.clear({ resetWidget: true });
          } catch {
            // Ignore errors during session clearing
          }
        }
      }

      // The tracking cookies are deliberately left alone. `hubspotutk` is what
      // carries a visitor's history from the marketing site into the contact they
      // become, so deleting it here threw that away on every identify. Chat
      // identity is handled by identificationEmail and the clear() above.

      // Read by widget.load(), which only runs when the chat is opened
      window.hsConversationsSettings = conversationsSettings(
        user.email,
        user.chatIdentificationToken,
      );

      // Use HubSpot tracking API to set contact properties
      window._hsq = window._hsq || [];
      window._hsq.push([
        "identify",
        {
          email: user.email,
          firstname: user.firstName,
          lastname: user.lastName,
          company: user.company,
        },
      ]);

      // Mark as identified
      setIsIdentified(true);

      // Now load the script (if not already loaded)
      // The identity is set BEFORE the script loads
      loadScript();
    },
    [loadScript],
  );

  return (
    <HubSpotContext.Provider
      value={{
        isLoaded,
        isLoading,
        isIdentified,
        error,
        unreadCount,
        isChatOpen,
        openChat,
        closeChat,
        toggleChat,
        identifyUser,
      }}
    >
      {children}
    </HubSpotContext.Provider>
  );
};

export const useHubSpot = (): HubSpotContextValue => {
  const context = useContext(HubSpotContext);
  if (!context) {
    throw new Error("useHubSpot must be used within a HubSpotProvider");
  }
  return context;
};
